import {
  LayoutDashboard, BarChart3, MessagesSquare, Users, Tags,
  FileText, Megaphone, CalendarClock, Bot, Zap,
  Smartphone, Plug, UserCog, FolderOpen, SendHorizontal, Settings,
  Activity,
  type LucideIcon,
} from 'lucide-react';

/**
 * Navegação como DADO.
 *
 * O menu anterior era markup escrito à mão: 16 <button>, cada um com um <img>
 * apontando para um SVG de pasta. O resultado medido no DOM renderizado:
 *
 *   - 11 ícones para 16 itens. `ads-click.svg` servia a quatro coisas sem
 *     relação (respostas automáticas, agendamentos, testar mensagem, agentes
 *     de IA); `table-view.svg` e `group-add.svg` a duas cada.
 *   - 12 arquivos com 9 viewBox diferentes, então o mesmo `width: 20px`
 *     produzia traço grosso num ícone e fino no vizinho.
 *   - Semântica errada em dois: um QR code representando "Etiquetas" e um
 *     ponto de interrogação representando "Configurações".
 *   - Uma lista plana de 11 itens seguida de um único grupo. Sem agrupamento
 *     por tarefa, achar "Modelos" exigia ler os 16 rótulos.
 *
 * Nada disso se resolve ajustando CSS: o problema é que a estrutura estava
 * espalhada por 16 blocos de HTML, onde nenhuma regra é verificável. Aqui é um
 * array — dá para contar, testar e revisar de uma vez.
 *
 * Família única (Lucide): mesma grade de 24, mesma espessura de traço, mesmo
 * arredondamento. Um ícone por item, sem reuso.
 */

export type ViewId =
  | 'overview' | 'metrics'
  | 'inbox' | 'contacts' | 'tags'
  | 'templates' | 'campaigns' | 'scheduled'
  | 'autoreplies' | 'ai-agents'
  | 'channels' | 'integrations' | 'integration-monitor' | 'agents' | 'files' | 'message-test' | 'settings';

export interface NavEntry {
  id: ViewId;
  label: string;
  icon: LucideIcon;
  /** Só ADMIN enxerga. A seção não some do menu para os demais: aparece
   *  desabilitada com o motivo. Sumir faz a pessoa procurar por uma função que
   *  existe e ela não pode acessar. */
  adminOnly?: boolean;
}

export interface NavGroup {
  /** Rótulo do grupo. Nomeia a TAREFA, não a entidade: "Campanhas" agrupa o
   *  que se faz para disparar mensagem, e não "coisas do tipo campanha". */
  label: string;
  items: readonly NavEntry[];
}

export const NAV: readonly NavGroup[] = [
  {
    label: 'Panorama',
    items: [
      { id: 'overview', label: 'Visão geral', icon: LayoutDashboard },
      { id: 'metrics', label: 'Métricas', icon: BarChart3, adminOnly: true },
    ],
  },
  {
    label: 'Atendimento',
    items: [
      { id: 'inbox', label: 'Conversas', icon: MessagesSquare },
      { id: 'contacts', label: 'Contatos', icon: Users },
      { id: 'tags', label: 'Etiquetas', icon: Tags },
    ],
  },
  {
    label: 'Campanhas',
    items: [
      { id: 'templates', label: 'Modelos', icon: FileText },
      { id: 'campaigns', label: 'Disparos', icon: Megaphone },
      { id: 'scheduled', label: 'Agendamentos', icon: CalendarClock },
    ],
  },
  {
    label: 'Automação',
    items: [
      { id: 'autoreplies', label: 'Respostas automáticas', icon: Zap },
      { id: 'ai-agents', label: 'Agentes de IA', icon: Bot, adminOnly: true },
    ],
  },
  {
    label: 'Configuração',
    items: [
      { id: 'channels', label: 'Números', icon: Smartphone },
      { id: 'integrations', label: 'Integrações', icon: Plug },
      { id: 'integration-monitor', label: 'Monitor de integrações', icon: Activity, adminOnly: true },
      { id: 'agents', label: 'Operadores', icon: UserCog, adminOnly: true },
      { id: 'files', label: 'Arquivos', icon: FolderOpen },
      { id: 'message-test', label: 'Testar mensagem', icon: SendHorizontal },
      { id: 'settings', label: 'Configurações', icon: Settings },
    ],
  },
] as const;

/** Achatado, para roteamento e para o teste que garante ícone único por item. */
export const NAV_ITEMS: readonly NavEntry[] = NAV.flatMap((group) => group.items);

export function findView(id: string): NavEntry | undefined {
  return NAV_ITEMS.find((item) => item.id === id);
}
